import { assertPublicUrl } from "../network/SourceRegistry.ts";
import { parseYomiriRepository } from './YomiriRepoParser.ts';
import type { SourceProfile } from '../source-engine/types.ts';

export interface RepoSource {
  contentKind?: 'manga' | 'anime' | 'live';
  connectorId?: string;
  profile?: SourceProfile;
  id: string;
  name: string;
  lang: string;
  baseUrl: string;
}
export interface RepoExtension {
  name: string;
  pkg: string;
  apk: string;
  lang: string;
  code: number;
  version: string;
  nsfw: boolean;
  sources: RepoSource[];
  iconUrl: string;
  iconFallbackUrl?: string;
}
export interface KeiyoushiRepository {
  url: string;
  baseUrl: string;
  name: string;
  format: "json" | "protobuf" | "yomiri";
  extensions: RepoExtension[];
}
const MAX_BYTES = 16 * 1024 * 1024;
export function normalizeRepoUrl(input: string): { url: string; baseUrl: string } {
  let cleaned = input.trim();
  if (!/^https?:\/\//i.test(cleaned)) cleaned = "https://" + cleaned;
  const url = assertPublicUrl(cleaned);
  url.hash = "";
  if (url.hostname === "github.com") {
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts.length >= 5 && ["raw", "blob"].includes(parts[2])) {
      const branchAndPath = parts.slice(parts[3] === "refs" && parts[4] === "heads" ? 5 : 3).join("/");
      return normalizeRepoUrl("https://raw.githubusercontent.com/" + parts[0] + "/" + parts[1] + "/" + branchAndPath);
    }
    if (parts.length >= 4 && parts[2] === "tree") {
      const branchAndPath = parts.slice(3).join("/");
      return normalizeRepoUrl("https://raw.githubusercontent.com/" + parts[0] + "/" + parts[1] + "/" + branchAndPath + "/index.min.json");
    }
    if (parts.length === 2) {
      return normalizeRepoUrl("https://raw.githubusercontent.com/" + parts[0] + "/" + parts[1] + "/main/index.min.json");
    }
  }
  if (!/\.(?:json|pb)$/i.test(url.pathname)) {
    if (!url.pathname.endsWith("/")) url.pathname += "/";
    url.pathname += "index.min.json";
  }
  const baseUrl = new URL("./", url).href;
  return { url: url.href, baseUrl };
}
function string(value: unknown, label: string): string {
  if (typeof value !== "string" || !value || value.length > 4096)
    throw new Error(`Repositorio: ${label} inválido.`);
  return value;
}
function entry(raw: unknown, baseUrl: string): RepoExtension {
  if (!raw || typeof raw !== "object") throw new Error("Entrada de repositorio inválida.");
  const e = raw as Record<string, unknown>;
  const pkg = string(e.pkg, "pkg");
  if (!/^[a-zA-Z0-9_.-]+$/.test(pkg)) throw new Error("Paquete inválido.");
  if (!Array.isArray(e.sources) || e.sources.length > 512)
    throw new Error("Lista de fuentes inválida.");
  const sources = e.sources.map((value): RepoSource => {
    if (!value || typeof value !== "object") throw new Error("Fuente inválida.");
    const s = value as Record<string, unknown>;
    const id = string(s.id, "source.id");
    if (!/^-?\d{1,20}$/.test(id)) throw new Error("ID de fuente inválido.");
    return {
      id,
      name: string(s.name, "source.name"),
      lang: string(s.lang, "source.lang"),
      baseUrl: typeof s.baseUrl === "string" && s.baseUrl
        ? s.baseUrl
        : typeof s.homeUrl === "string"
          ? s.homeUrl
          : "",
    };
  });
  const code = Number(e.code ?? 0);
  if (!Number.isSafeInteger(code) || code < 0) throw new Error("Versión numérica inválida.");
  let iconFallbackUrl: string | undefined;
  try {
    if (typeof e.iconFallbackUrl === "string" && e.iconFallbackUrl)
      iconFallbackUrl = assertPublicUrl(e.iconFallbackUrl).href;
  } catch {
    /* Ignore invalid icon metadata. */
  }
  return {
    name: string(e.name, "name"),
    pkg,
    apk: typeof e.apk === "string" ? e.apk : "",
    lang: typeof e.lang === "string" ? e.lang : (sources[0]?.lang ?? "all"),
    code,
    version: string(e.version, "version"),
    nsfw: e.nsfw === true || (typeof e.nsfw === "number" && e.nsfw !== 0),
    sources,
    iconUrl: new URL(`icon/${pkg}.png`, baseUrl).href,
    ...(iconFallbackUrl ? { iconFallbackUrl } : {}),
  };
}
export function parseKeiyoushiJson(input: unknown, baseUrl: string): RepoExtension[] {
  if (!Array.isArray(input) || !input.length || input.length > 10000)
    throw new Error("Catálogo JSON inválido.");
  return input.map((value) => entry(value, baseUrl));
}
type WireField = { tag: number; value: bigint | Uint8Array };
function wire(bytes: Uint8Array): WireField[] {
  let offset = 0;
  const fields: WireField[] = [];
  const readVarint = () => {
    let value = 0n;
    for (let i = 0; i < 10; i++) {
      if (offset >= bytes.length) throw new Error("Protobuf truncado.");
      const b = bytes[offset++];
      if (i === 9 && b > 1) throw new Error("Varint fuera de rango.");
      value |= BigInt(b & 127) << BigInt(i * 7);
      if (!(b & 128)) return value;
    }
    throw new Error("Varint inválido.");
  };
  while (offset < bytes.length) {
    if (fields.length >= 50000) throw new Error("Demasiados campos Protobuf.");
    const key = readVarint(),
      tag = Number(key >> 3n),
      kind = Number(key & 7n);
    if (!tag || tag > 536870911) throw new Error("Tag Protobuf inválido.");
    if (kind === 0) fields.push({ tag, value: readVarint() });
    else if (kind === 2) {
      const size = Number(readVarint());
      if (!Number.isSafeInteger(size) || size > MAX_BYTES || offset + size > bytes.length)
        throw new Error("Campo Protobuf truncado.");
      fields.push({ tag, value: bytes.subarray(offset, offset + size) });
      offset += size;
    } else if (kind === 1 || kind === 5) {
      offset += kind === 1 ? 8 : 4;
      if (offset > bytes.length) throw new Error("Protobuf truncado.");
    } else throw new Error("Wire type Protobuf no admitido.");
  }
  return fields;
}
const text = (fields: WireField[], tag: number) => {
  const value = fields.find((f) => f.tag === tag)?.value;
  return value instanceof Uint8Array ? new TextDecoder("utf-8", { fatal: true }).decode(value) : "";
};
const number = (fields: WireField[], tag: number) => {
  const value = fields.find((f) => f.tag === tag)?.value;
  return typeof value === "bigint" ? Number(value) : 0;
};
export function parseKeiyoushiProtobuf(
  bytes: Uint8Array,
  baseUrl: string,
): { name: string; extensions: RepoExtension[] } {
  if (bytes.length > MAX_BYTES) throw new Error("Catálogo demasiado grande.");
  const root = wire(bytes);
  const modern = root.find((f) => f.tag === 101)?.value;
  const rows = modern instanceof Uint8Array ? wire(modern) : root;
  const messages = rows.filter((f) => f.tag === 1 && f.value instanceof Uint8Array);
  if (!messages.length || messages.length > 10000)
    throw new Error("Catálogo Protobuf vacío o inválido.");
  const extensions = messages.map((message) => {
    const fields = wire(message.value as Uint8Array);
    const sources = fields
      .filter((f) => f.tag === 8 && f.value instanceof Uint8Array)
      .map((f) => {
        const source = wire(f.value as Uint8Array);
        const id = source.find((v) => v.tag === 1)?.value;
        return {
          id: typeof id === "bigint" ? BigInt.asIntN(64, id).toString() : text(source, 1),
          name: text(source, 2),
          lang: text(source, 3),
          baseUrl: text(source, 4),
        };
      });
    let apk = modern ? "" : text(fields, 3);
    let iconFallbackUrl = "";
    if (modern instanceof Uint8Array) {
      const assets = fields.find((f) => f.tag === 3)?.value;
      apk = assets instanceof Uint8Array ? text(wire(assets), 1) : "";
      iconFallbackUrl = assets instanceof Uint8Array ? text(wire(assets), 2) : "";
    }
    return entry(
      {
        name: text(fields, 1),
        pkg: text(fields, 2),
        apk,
        iconFallbackUrl,
        lang: modern
          ? new Set(sources.map((s) => s.lang)).size === 1
            ? sources[0]?.lang
            : "all"
          : text(fields, 4),
        code: number(fields, 5),
        version: text(fields, 6),
        nsfw: modern ? number(fields, 7) === 3 : number(fields, 7),
        sources,
      },
      baseUrl,
    );
  });
  return { name: modern ? text(root, 1) || "Keiyoushi" : "Keiyoushi", extensions };
}
async function bounded(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.length;
      if (size > MAX_BYTES) {
        await reader.cancel();
        throw new Error("Catálogo excede 16 MiB.");
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
}
export async function decodeRepoBytes(bytes: Uint8Array): Promise<Uint8Array> {
  if (bytes[0] === 0x1f && bytes[1] === 0x8b)
    return bounded(
      new Blob([new Uint8Array(bytes).buffer])
        .stream()
        .pipeThrough(new DecompressionStream("gzip")),
    );
  return bytes;
}
export async function loadKeiyoushiRepository(
  input: string,
  options: { forceProtobuf?: boolean; fetcher?: typeof fetch; signal?: AbortSignal } = {},
): Promise<KeiyoushiRepository> {
  const { url, baseUrl } = normalizeRepoUrl(input);
  const fetcher = options.fetcher ?? fetch;
  const read = async (path: string) => {
    const response = await fetcher(new URL(path, baseUrl).href, {
      signal: options.signal ?? AbortSignal.timeout(15000),
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) throw new Error(`Repositorio: HTTP ${response.status}.`);
    if (!response.body) throw new Error("Repositorio vacío.");
    return bounded(response.body);
  };
  if (!options.forceProtobuf)
    try {
      const input: unknown = JSON.parse(new TextDecoder().decode(await read(new URL(url).pathname.endsWith('.json') ? url : 'index.min.json')));
      if (input && !Array.isArray(input) && typeof input === 'object' && 'format' in input) {
        const parsed = parseYomiriRepository(input);
        return { url, baseUrl, format: 'yomiri', ...parsed };
      }
      const extensions = parseKeiyoushiJson(input, baseUrl);
      const notices = extensions.every((e) => /\.all\.(keiyoushi|mihon)$/.test(e.pkg));
      if (!notices)
        return {
          url,
          baseUrl,
          name:
            new URL(baseUrl).hostname === "raw.githubusercontent.com" && new URL(baseUrl).pathname.startsWith('/yuzono/')
              ? "Yūzōnō"
              : new URL(baseUrl).hostname === "raw.githubusercontent.com"
              ? "Keiyoushi"
              : new URL(baseUrl).hostname,
          format: "json",
          extensions,
        };
    } catch (error) {
      if (options.signal?.aborted) throw error;
      const file=new URL(url).pathname.split('/').pop()||'';
      if(file.endsWith('.json')&&!['index.min.json','index.json'].includes(file))throw error;
    }
  const parsed = parseKeiyoushiProtobuf(await decodeRepoBytes(await read("index.pb")), baseUrl);
  return { url, baseUrl, format: "protobuf", ...parsed };
}
